import { useEffect, useMemo, useState } from 'react';
import type { TeamNotifRecipients } from '@/proto/logiblocks/logiblocks';
import { CustomSelectIcon } from '@/assets/icons/icons';
import TeamUserSelect, {
  type TeamData,
  type TeamUserSelection,
} from '@/components/TeamUserSelect';
import { GetUsersByTeamIds } from '@/apis/settings';
import useToken from '@/common/helper/useToken';
import { useTeamGroups } from '@/hooks/useTeamGroups';
import {
  apiTeamsResponseToAdditionalOptions,
  apiTeamsResponseToTeamData,
  teamNotifRecipientsToTeamUserSelection,
  teamUserSelectionToTeamNotifRecipients,
  type AdditionalOption,
} from '../emailAction/recipientUtils';

interface DelayNotifyRecipientsProps {
  value: TeamNotifRecipients[];
  onChange: (recipients: TeamNotifRecipients[]) => void;
  disabled?: boolean;
}

/**
 * "Recipients" picker for "Notify someone else". The same teams-and-users
 * picker the Email node uses, stored as the same TeamNotifRecipients entries.
 */
export const DelayNotifyRecipients = ({
  value,
  onChange,
  disabled = false,
}: DelayNotifyRecipientsProps) => {
  const { getToken } = useToken();
  const [teamsData, setTeamsData] = useState<TeamData[]>([]);
  const [additionalOptions, setAdditionalOptions] = useState<
    AdditionalOption[]
  >([]);
  const [loading, setLoading] = useState(true);
  const teamGroups = useTeamGroups(teamsData.map((team) => team.teamId));

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    GetUsersByTeamIds(token, [])
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          setTeamsData(
            apiTeamsResponseToTeamData(data?.data?.teams_users ?? [])
          );
          setAdditionalOptions(
            apiTeamsResponseToAdditionalOptions(data?.data?.additional ?? [])
          );
        }
      })
      .catch((err) => {
        console.error('Failed to fetch teams for delay notification', err);
      })
      .finally(() => setLoading(false));
  }, [getToken]);

  const selection = useMemo(
    () => teamNotifRecipientsToTeamUserSelection(value),
    [value]
  );

  if (loading) {
    return <div>Loading teams...</div>;
  }

  return (
    <TeamUserSelect
      teamsData={teamsData}
      additionalOptions={additionalOptions}
      teamGroups={teamGroups}
      value={selection}
      onChange={(next: TeamUserSelection) =>
        onChange(teamUserSelectionToTeamNotifRecipients(next, teamsData))
      }
      disabled={disabled}
      placeholder='Select...'
      chevron={<CustomSelectIcon />}
    />
  );
};
